import type { TableDataJSON } from '../../src/types/table-json'
import type { ReactNode } from 'react'
import type { CalculatorDescription } from './calculator-description'
import type { EChartsOption } from 'echarts'
import type { AnovaResult } from '../../components/calculators/anova/anova-calculator'
import type { BinomialResult } from '../../components/calculators/binomial-distribution/binomial-calculator'
import type { NormalResult } from '../../components/calculators/normal-distribution/normal-calculator'
import type { ChiSquareResult } from '../../components/calculators/chi-square/chi-square-calculator'
import type { CoefficientResult } from '../../components/calculators/coefficient-of-variation/coefficient-of-variation-calculator'
import { confidenceIntervalControls } from '../../components/calculators/confidence-interval/confidence-interval.config'
import type { ConfidenceIntervalResult } from '../../components/calculators/confidence-interval/confidence-interval-calculator'
import type { CovarianceResult } from '../../components/calculators/covariance/covariance-calculator'
import type { IQRResult } from '../../lib/stats/quartiles'
import type { KurtosisResult } from '../../lib/stats/kurtosis'
import type { MedianResult } from '../../lib/stats/median'
import type { ModeResult } from '../../lib/stats/mode'
import type { PercentileResult } from '../../lib/stats/percentile'
import type { QuartileResult } from '../../lib/stats/quartile'
import type { RangeResult } from '../../lib/stats/range'
import type { StandardDeviationResult } from '../../lib/stats/standard-deviation'
import type { VarianceResult } from '../../lib/stats/variance'
import type { StatisticalSummaryResult } from '../../lib/stats/statistical-summary'
import type { ZScoreResult } from '../../lib/stats/z-score'
import type { MeanResult } from '../../lib/stats/mean'

export enum ControlType {
  Text = 'text',
  Number = 'number',
  Select = 'select',
  SingleColumnSelect = 'single-column-select',
  MultiColumnSelect = 'multi-column-select'
}

/**
 * Unique identifiers for calculators.
 * The format is '{toolType}/{slug}' which maps directly to the URL path.
 * For example:
 * - 'graphs/bar' -> /graphs/bar
 * - 'graphs/pie' -> /graphs/pie
 */
export enum CalculatorId {
  BarGraph = 'graphs/bar',
  StackedBarChart = 'graphs/stacked-bar',
  HorizontalBarGraph = 'graphs/horizontal-bar',
  PieChart = 'graphs/pie',
  DonutChart = 'graphs/donut',
  NightingaleChart = 'graphs/nightingale-rose',
  ColumnChart = 'graphs/column',
  ScatterChart = 'graphs/scatter',
  XYPlot = 'graphs/x-y',
  LineChart = 'graphs/line',
  AreaChart = 'graphs/area',
  Histogram = 'graphs/histogram',
  RadarChart = 'graphs/radar',
  BoxPlot = 'graphs/box-plot',
  Heatmap = 'graphs/heatmap',
  SunburstChart = 'graphs/sunburst',
  TreeChart = 'graphs/tree',
  SankeyChart = 'graphs/sankey',
  TimeseriesChart = 'graphs/timeseries',
  ANOVA = 'statistics/anova',
  BINOMIAL_DISTRIBUTION = 'statistics/binomial-distribution',
  NORMAL_DISTRIBUTION = 'statistics/normal-distribution',
  POISSON_DISTRIBUTION = 'statistics/poisson-distribution',
  CHI_SQUARE = 'statistics/chi-square',
  COEFFICIENT_OF_VARIATION = 'statistics/coefficient-of-variation',
  STANDARD_DEVIATION = 'statistics/standard-deviation',
  MEAN = 'statistics/mean',
  VARIANCE = 'statistics/variance',
  CONFIDENCE_INTERVAL = 'statistics/confidence-interval',
  CORRELATION_COEFFICIENT = 'statistics/correlation-coefficient',
  COVARIANCE = 'statistics/covariance',
  FIVE_NUMBER_SUMMARY = 'statistics/five-number-summary',
  INTERQUARTILE_RANGE = 'statistics/interquartile-range',
  KURTOSIS = 'statistics/kurtosis',
  MEDIAN = 'statistics/median',
  MODE = 'statistics/mode',
  PERCENTILE = 'statistics/percentile',
  QUARTILE = 'statistics/quartile',
  RANGE = 'statistics/range',
  SUMMARY = 'statistics/summary',
  Z_SCORE = 'statistics/z-score',
  MEAN_ABSOLUTE_DEVIATION = 'statistics/mean-absolute-deviation',
  MEDIAN_ABSOLUTE_DEVIATION = 'statistics/median-absolute-deviation',
  CRITICAL_VALUE = 'statistics/critical-value',
  COEFFICIENT_OF_DETERMINATION = 'statistics/coefficient-of-determination',
  DEGREES_OF_FREEDOM = 'statistics/degrees-of-freedom',
  SUM_SQUARED_ERRORS = 'statistics/sum-squared-errors'
}

// Type assertion to ensure CalculatorId values can be used as keys
export type CalculatorIdType = keyof typeof CalculatorId;

export enum CalculatorType {
  Graph = 'graph',
  Table = 'table'
}

export enum DataTableInputMode {
  Required = 'required',
  None = 'none'
}

export const ControlIds = {
  Category: 'category',
  Values: 'values',
  Value: 'value',
  Title: 'title',
  BarLayout: 'barLayout',
  Orientation: 'orientation',
  BarWidth: 'barWidth',
  EnableShadow: 'enableShadow',
  ShowLabels: 'showLabels',
  LabelPosition: 'labelPosition',
  ShowPercentages: 'showPercentages',
  Radius: 'radius',
  InnerRadius: 'innerRadius',
  CenterText: 'centerText',
  SortValues: 'sortValues',
  RoseType: 'roseType',
  XColumn: 'xColumn',
  YColumn: 'yColumn',
  MarkerSize: 'markerSize',
  ShowTrendline: 'showTrendline',
  TrendlineType: 'trendlineType',
  DataColumn: 'dataColumn',
  BinCount: 'binCount',
  BarGap: 'barGap',
  // Radar chart controls
  AxisMax: 'axisMax',
  ShowArea: 'showArea',
  AreaOpacity: 'areaOpacity',
  // Box plot controls
  WhiskerLength: 'whiskerLength',
  ShowOutliers: 'showOutliers',
  BoxWidth: 'boxWidth',
  // ANOVA controls
  Alpha: 'alpha',
  Successes: 'successes',
  Trials: 'trials',
  Probability: 'probability',
  Mean: 'mean',
  StandardDeviation: 'standardDeviation',
  // Heatmap controls
  ColorScheme: 'colorScheme',
  // Z-score controls
  InputValue: 'inputValue'
} as const;

export type ControlId = typeof ControlIds[keyof typeof ControlIds] | string;

// Base control type with common properties
export type BaseControl = {
  id: ControlId;
  type: ControlType;
  label: string;
  required?: boolean;
  defaultValue?: unknown;
};

// Specific control types
export type TextControl = BaseControl & {
  type: ControlType.Text;
  defaultValue?: string;
};

export type NumberControl = BaseControl & {
  type: ControlType.Number;
  defaultValue?: number | string;
  min?: number;
  max?: number;
  step?: number;
};

export type SelectControl = BaseControl & {
  type: ControlType.Select;
  options: { label: string; value: string }[];
  defaultValue?: string;
};

export type SingleColumnSelectControl = BaseControl & {
  type: ControlType.SingleColumnSelect;
  numericOnly?: boolean;
  defaultValue?: string;
};

export type MultiColumnSelectControl = BaseControl & {
  type: ControlType.MultiColumnSelect;
  numericOnly?: boolean;
  minSelections?: number;
  maxSelections?: number;
  defaultValue?: string[];
};

// Union of all control types
export type CalculatorControl =
  | TextControl
  | NumberControl
  | SelectControl
  | SingleColumnSelectControl
  | MultiColumnSelectControl;

// Define the control values type
export interface ControlValues {
  [key: string]: string | string[] | number | boolean | undefined;
}

// Calculator configuration type
export interface CalculatorConfig<T extends keyof CalculatorTypes> {
  id: T;
  type: CalculatorType;
  name: string;
  dataTableInputMode?: DataTableInputMode;
  controls: {
    tabs: {
      title: string;
      controls: readonly CalculatorControl[];
    }[];
  };
  initialData?: TableDataJSON;
  initialControlValues?: Partial<ControlValues>;
  getInitialConfig: (data: TableDataJSON) => Partial<ControlValues>;
  getVisualizationData: (params: {
    data: TableDataJSON;
    controls: ControlValues;
  }) => CalculatorTypes[T]['visualization'] | null;
  render: (params: { visualizationData: CalculatorTypes[T]['visualization'] | null }) => ReactNode;
}

// Define visualization types for each calculator
export type CalculatorTypes = {
  [key in CalculatorId]: {
    controls: readonly CalculatorControl[];
    visualization: key extends CalculatorId.BarGraph | CalculatorId.StackedBarChart | CalculatorId.HorizontalBarGraph | 
                           CalculatorId.PieChart | CalculatorId.DonutChart | CalculatorId.NightingaleChart | 
                           CalculatorId.ColumnChart | CalculatorId.ScatterChart | CalculatorId.XYPlot | 
                           CalculatorId.LineChart | CalculatorId.AreaChart | CalculatorId.Histogram | 
                           CalculatorId.RadarChart | CalculatorId.BoxPlot | CalculatorId.Heatmap | 
                           CalculatorId.SunburstChart | CalculatorId.TreeChart | CalculatorId.SankeyChart | 
                           CalculatorId.TimeseriesChart
                           ? EChartsOption
                           : key extends CalculatorId.ANOVA ? AnovaResult
                           : key extends CalculatorId.BINOMIAL_DISTRIBUTION ? BinomialResult
                           : key extends CalculatorId.POISSON_DISTRIBUTION ? import('../../components/calculators/poisson-distribution/poisson-calculator').PoissonResult
                           : key extends CalculatorId.NORMAL_DISTRIBUTION ? NormalResult
                           : key extends CalculatorId.CHI_SQUARE ? ChiSquareResult
                           : key extends CalculatorId.COEFFICIENT_OF_VARIATION ? CoefficientResult
                           : key extends CalculatorId.CONFIDENCE_INTERVAL ? ConfidenceIntervalResult
                           : key extends CalculatorId.CORRELATION_COEFFICIENT ? import('../../components/calculators/correlation-coefficient/correlation-coefficient-calculator').CorrelationResult
                           : key extends CalculatorId.COVARIANCE ? CovarianceResult
                           : key extends CalculatorId.FIVE_NUMBER_SUMMARY ? import('../stats/types').BoxPlotStats
                           : key extends CalculatorId.INTERQUARTILE_RANGE ? IQRResult
                           : key extends CalculatorId.KURTOSIS ? KurtosisResult
                           : key extends CalculatorId.MEAN ? MeanResult
                           : key extends CalculatorId.MEDIAN ? MedianResult
                           : key extends CalculatorId.MODE ? ModeResult
                           : key extends CalculatorId.PERCENTILE ? PercentileResult
                           : key extends CalculatorId.QUARTILE ? QuartileResult
                           : key extends CalculatorId.RANGE ? RangeResult
                           : key extends CalculatorId.STANDARD_DEVIATION ? StandardDeviationResult
                           : key extends CalculatorId.VARIANCE ? VarianceResult
                           : key extends CalculatorId.SUMMARY ? StatisticalSummaryResult
                           : key extends CalculatorId.Z_SCORE ? ZScoreResult
                           : key extends CalculatorId.MEAN_ABSOLUTE_DEVIATION ? import('../stats/mean-absolute-deviation').MeanAbsoluteDeviationResult
                           : key extends CalculatorId.MEDIAN_ABSOLUTE_DEVIATION ? import('../stats/median-absolute-deviation').MedianAbsoluteDeviationResult
                           : key extends CalculatorId.CRITICAL_VALUE ? import('../stats/critical-value').CriticalValueResult
                           : key extends CalculatorId.COEFFICIENT_OF_DETERMINATION ? import('../stats/coefficient-of-determination').CoefficientOfDeterminationResult
                           : key extends CalculatorId.DEGREES_OF_FREEDOM ? import('../stats/degrees-of-freedom').DegreesOfFreedomResult
                           : key extends CalculatorId.SUM_SQUARED_ERRORS ? import('../stats/sum-squared-errors').SumSquaredErrorsResult
                           : never;
  };
}; 